import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Container, Row, Col, Card, Table, Modal, Button, Form, Spinner } from "react-bootstrap";
import ClickBack from "../../../components/buttons/ClickBack";
import IconEdit from "../../../images/edit.png";
import moment from "moment";
import { GetOneCommandeById, genererAvoir, genererAvoirPartiel } from "./DataService";
import { urlPdf } from "../../../utils/ApiConfig";
import PODImages from "../Component/PODImages";
import CommandeFeedback from "../Component/CommandeFeedback";
import TempsSurPlace from "../Component/TempsSurPlace";
import { libelleAscenseur } from "../../../utils/libelleAscenseur";

// Trois sources renseignent la manutention (voir SaisieManutentionService côté API).
const LIBELLES_SAISIE_MANUTENTION = {
  commanditaire: "le client, à la commande",
  ops: "l'agence",
  livreur: "le livreur, après la livraison",
};

function ShowCommandeFournisseur() {
  const [orderData, setOrderData] = useState();
  const { idCommande } = useParams();
  const [avoirModal, setAvoirModal] = useState(false);
  const [avoirMotif, setAvoirMotif] = useState("");
  const [avoirLoading, setAvoirLoading] = useState(false);
  const [avoirError, setAvoirError] = useState("");

  // Avoir à montant libre : document comptable seul, aucun remboursement.
  const [partielModal, setPartielModal] = useState(false);
  const [partielMontant, setPartielMontant] = useState("");
  const [partielMotif, setPartielMotif] = useState("");
  const [partielLoading, setPartielLoading] = useState(false);
  const [partielError, setPartielError] = useState("");
  const [partielSucces, setPartielSucces] = useState(null);

  // Bordereau en grand : l'apercu en colonne reste utile pour reperer le
  // document, mais un bon de livraison manuscrit ne se lit pas a cette taille.
  const [bordereauModal, setBordereauModal] = useState(false);

  const fetchOrderData = async (idCommande) => {
    try {
      const order = await GetOneCommandeById(idCommande);
      
      setOrderData(order);
    } catch (error) {
      console.error("Error fetching order data:", error);
    }
  };
  //Fonction pour définir les status commande fournisseur
  const mapStatusCommande = (statusID) => {
    switch (statusID) {
      case 1:
        return "En cours de livraison";
      case 2:
        return "Livré";
      case 3:
        return "En cours de préparation";
      case 4:
        return "En cours de validation";
      case 5:
        return "Validé";
      case 6:
        return "Annulé";
      default:
        return "";
    }
  };
  //Fonction pour définir les status commande fournisseur
  const mapStatusPaiement = (statusID) => {
    switch (statusID) {
      case 1:
        return "Facturé";
      case 2:
        return "Non Facturé";
      case 3:
        return "Paiement réussie (marion-livraison)";
      case 4:
        return "Paiement non réussie  (marion-livraison)";
      case 5:
        return "Paiement annulé";
      case 6:
        return "Offert";
      case 7:
        return "Remboursé";
      default:
        return "";
    }
  };

  const handleGenererAvoir = async () => {
    if (!avoirMotif.trim()) { setAvoirError("Le motif est requis."); return; }
    setAvoirLoading(true);
    setAvoirError("");
    try {
      await genererAvoir(idCommande, avoirMotif);
      setAvoirModal(false);
      setAvoirMotif("");
      fetchOrderData(idCommande);
    } catch (e) {
      setAvoirError(e?.response?.data?.error || "Erreur inconnue.");
    } finally {
      setAvoirLoading(false);
    }
  };

  const handleAvoirPartiel = async () => {
    const montant = parseFloat(String(partielMontant).replace(",", "."));

    if (!montant || montant <= 0) {
      setPartielError("Indiquer un montant HT supérieur à 0.");
      return;
    }
    if (!partielMotif.trim()) {
      setPartielError("Le motif est requis : il figure sur le document remis au client.");
      return;
    }

    setPartielLoading(true);
    setPartielError("");
    try {
      const res = await genererAvoirPartiel(idCommande, montant, partielMotif);
      setPartielSucces(res);
      setPartielMontant("");
      setPartielMotif("");
      fetchOrderData(idCommande);
    } catch (e) {
      setPartielError(e?.response?.data?.error || "Erreur inconnue.");
    } finally {
      setPartielLoading(false);
    }
  };

  useEffect(() => {
    fetchOrderData(idCommande);
  }, [idCommande]);

  if (!orderData) {
    return (
      <Container>
        <p>Loading...</p>
      </Container>
    );
  }

  // `bordereau_url` est un accesseur du modele, present dans $appends : il
  // normalise deja le prefixe « Bordereau/ », present sur certaines lignes et
  // absent sur d'autres. Le recomposer ici en ferait une seconde version.
  const urlBordereau = orderData?.bordereau_url
    || (orderData?.Bordereau ? `${urlPdf}/Bordereau/${orderData.Bordereau}` : null);

  // Une photo de bon de livraison s'affiche mieux en <img> qu'en <iframe>, qui
  // la cadre sans permettre de zoomer.
  const bordereauEstImage = /\.(png|jpe?g|webp|gif|heic)$/i.test(orderData?.Bordereau || "");

  return (
    <Container>
      <div className="orderDetails">
        <p className="h6">
          {/* Breadcrumb navigation */}
          Accueil / Livraisons
        </p>

        <Row className="pt-4 pb-2">
          <Col style={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
            <ClickBack editPage={true} />
            <h1 className="p-1">Livraison Num ° {orderData?.id}</h1>
            {/* Même icône et même cible que la liste, pour qu'elle soit reconnue
                d'un écran à l'autre. */}
            <a
              href={`/commande-fournisseur/edit/${orderData?.id}`}
              className="p-2"
              title="Modifier cette livraison"
            >
              <img src={IconEdit} width="20px" alt="Modifier" />
            </a>
          </Col>
        </Row>

        {/* Une commande déjà facturée peut légitimement être corrigée (adresse,
            livreur), mais modifier ses montants ne change pas le document déjà
            envoyé au client. Rien ne le signalait, et c'est exactement ainsi que
            la facture 876 s'est mise à contredire sa commande. */}
        {[1, 3].includes(orderData?.StatuPaiement) && (
          <div
            className="mb-3 px-3 py-2"
            style={{
              background: "#fffbeb",
              borderLeft: "4px solid #f59e0b",
              borderRadius: "6px",
              fontSize: "0.9rem",
              color: "#78350f",
            }}
          >
            Commande déjà facturée ({mapStatusPaiement(orderData?.StatuPaiement)}) — modifier les
            montants ne changera pas la facture émise.
            <Button
              variant="outline-warning"
              size="sm"
              className="ms-3"
              onClick={() => {
                setPartielError("");
                setPartielSucces(null);
                setPartielModal(true);
              }}
            >
              Émettre un avoir
            </Button>
          </div>
        )}

        {partielSucces && (
          <div
            className="mb-3 px-3 py-2"
            style={{
              background: "#ecfdf5",
              borderLeft: "4px solid #10b981",
              borderRadius: "6px",
              fontSize: "0.9rem",
              color: "#065f46",
            }}
          >
            Avoir <strong>{partielSucces.avoir_number}</strong> émis pour{" "}
            {Number(partielSucces.montant_ttc).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} € TTC.{" "}
            <a
              href={`${urlPdf}/factures/${partielSucces.avoir_file}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Ouvrir le document
            </a>
          </div>
        )}

        <Card className="mb-3">
          <Card.Body>
            <Card.Title className="pb-3">Détails de la commande</Card.Title>
            <Row className="pb-3">
              <Col>
                <p>
                  <strong>Fournisseur: </strong>
                  {
                    orderData?.fournisseur_profil?.user_profil?.RaisonSocial
                  }{" "}
                </p>
              </Col>
              <Col>
                <p>
                  <strong>Email Fournisseur: </strong>
                  {orderData?.emailFournisseur}
                </p>
              </Col>
              <Col>
                <p>
                  <strong>Téléphone fournisseur: </strong>
                  {orderData?.TelFournisseur}
                </p>
              </Col>
            </Row>
            <Row className="pb-3">
              <Col>
                <p>
                  <strong>Nom Client: </strong>
                  {orderData?.NomClient}
                </p>
              </Col>
              <Col>
                <p>
                  <strong>Prénom Client: </strong>
                  {orderData?.PrenomClient}
                </p>
              </Col>
              <Col>
                <p>
                  <strong>Téléphone Client: </strong>
                  {orderData?.TelClient}
                </p>
              </Col>
            </Row>
            <Row className="pb-3">
              <Col>
                <p>
                  <strong>Raison Social Client: </strong>
                  {orderData?.RaisonSocialClient}
                </p>
                {/* La reference que le client a donnee a sa commande : elle figure
                    sur sa facture, donc l'ops doit pouvoir la lire ici avant de
                    repondre a une question de rapprochement. Absente, on l'ecrit,
                    plutot que de laisser un vide qu'on prendrait pour un bug. */}
                <p>
                  <strong>Votre référence (client): </strong>
                  {/* `??` et non `||` : une reference valant "0" est falsy en JS
                      comme en PHP, et un `||` l'aurait remplacee par « non
                      renseignee ». Le serveur garantit null-ou-non-vide. */}
                  {orderData?.reference_client ?? (
                    <span className="text-muted">non renseignée</span>
                  )}
                </p>
              </Col>
              <Col>
                <p>
                  <strong>Date de commande: </strong>
                  {moment(orderData?.DateCommande).format("DD-MM-YYYY HH:mm")}
                </p>
              </Col>
              <Col>
                <p>
                  <strong>Date de Livraisson: </strong>
                  {moment(orderData?.DateLivraison).format("DD-MM-YYYY HH:mm")}
                </p>
              </Col>
            </Row>

            <Row className="pb-3">
              <Col>
                <p>
                  <strong>Livreur: </strong>
                  {orderData?.livreur_profil?.nom}{" "}
                  {orderData?.livreur_profil?.prenom}
                </p>
              </Col>
              <Col>
                <p>
                  <strong>Adresse Pick Up: </strong>
                  {orderData?.AdressePickUp}
                </p>
              </Col>
              <Col>
                <p>
                  <strong>Adresse Livraison: </strong>
                  {orderData?.AdresseLivraison}
                </p>
              </Col>
            </Row>
            <Row className="pb-3">
              <Col>
                <p>
                  <strong>Statut de Paiement: </strong>
                  {mapStatusPaiement(orderData?.StatuPaiement)}
                </p>
              </Col>
              <Col>
                <p>
                  <strong>Statut de Commande: </strong>
                  {mapStatusCommande(orderData?.StatuCommande)}
                </p>
              </Col>
              <Col>
                <p>
                  <strong>Numéro Bon de Livraison : </strong>
                  {orderData?.NBonLivraison}
                </p>
              </Col>
            </Row>
            {orderData?.StatuCommande === 6 && orderData?.cancellation_reason && (
              <Row className="pb-3">
                <Col>
                  <p className="mb-0">
                    <strong>Motif d'annulation : </strong>
                    {orderData.cancellation_reason}
                  </p>
                </Col>
              </Row>
            )}
            <Row className="pb-3">
              <Col>
                <p>
                  <strong>Montant de la commande: </strong>
                  {orderData?.MontantCommande}
                </p>
              </Col>
              <Col>
                <p>
                  <strong>Tarif de la Livraison : </strong>
                  {orderData?.livraisonHT}
                </p>
              </Col>
              {/* Jalon posé par le livreur au départ vers l'enlèvement, avant le
                  chargement (passage en « en cours de livraison »). */}
              <Col>
                <p>
                  <strong>Livreur en route vers l'enlèvement : </strong>
                  {orderData?.ramassage_en_route_le
                    ? moment(orderData?.ramassage_en_route_le).format("DD-MM-YYYY HH:mm")
                    : "-"}
                </p>
              </Col>
              <Col>
                <p>
                  <strong>Date de livraison réelle : </strong>
                  {orderData?.DateLivraisonReelle
                    ? moment(orderData?.DateLivraisonReelle).format("DD-MM-YYYY HH:mm")
                    : "-"}
                </p>
              </Col>
            </Row>
            <Row className="pb-3">
              <Col>
                <p className="mb-0">
                  <strong>Temps sur place chez le client : </strong>
                  <TempsSurPlace idCommande={idCommande} statut={orderData?.StatuCommande} />
                </p>
              </Col>
            </Row>
            <Row className="pb-3">
              <Col>
                <p>
                  <strong>TVA: </strong>
                  {orderData?.TVA}
                </p>
              </Col>
              <Col>
                <p>
                  <strong>Montant TTC: </strong>
                  {orderData?.TTC}
                </p>
              </Col>
              <Col>
                <p>
                  <strong>Commentaire: </strong>
                  {orderData?.Commentaire || "-"}
                </p>
              </Col>
            </Row>
            <Row className="pb-3">
            <Col>
              <p><strong>Poids : </strong>{orderData?.poids}</p>
            </Col>
            <Col>
              <p><strong>Étiquette poids : </strong>{orderData?.poids_label}</p>
            </Col>
            <Col>
              <p><strong>Supplément poids : </strong>{orderData?.poids_supplement}</p>
            </Col>
            </Row>

          <Row className="pb-3">
            <Col>
              <p><strong>Temps estimé : </strong>{orderData?.temps_estime}</p>
            </Col>
            <Col>
              <p><strong>Supplément temps : </strong>{orderData?.temps_supplement}</p>
            </Col>
            <Col>
              <p><strong>Tarif base : </strong>{orderData?.tarif_base}</p>
            </Col>
          </Row>

          <Row className="pb-3">
            <Col>
              <p><strong>Tarif HT : </strong>{orderData?.tarif_ht}</p>
            </Col>
            <Col>
              <p><strong>Tarif TTC : </strong>{orderData?.tarif_ttc}</p>
            </Col>
            <Col>
              <p><strong>Type livraison : </strong>{orderData?.type_livraison}</p>
            </Col>
          </Row>

          <Row className="pb-3">
            <Col>
              <p><strong>Supplément livraison : </strong>{orderData?.livraison_supplement}</p>
            </Col>
            <Col>
              {orderData?.code_promo ? (
                <p>
                  <strong>Code promo : </strong>
                  <span style={{ color: "#e67e00", fontWeight: 600 }}>
                    {orderData.code_promo.code}
                  </span>
                  {orderData.code_promo.reduction_percent > 0
                    ? ` (-${orderData.code_promo.reduction_percent}%)`
                    : orderData.code_promo.reduction > 0
                    ? ` (-${orderData.code_promo.reduction} €)`
                    : ""}
                </p>
              ) : (
                <p><strong>Code promo : </strong>—</p>
              )}
            </Col>
            <Col />
          </Row>

          </Card.Body>
        </Card>

        {/* Bloc partenaire — affiché uniquement si source=3 */}
        {orderData?.source === 3 && (
          <Card className="mb-3 border-primary">
            <Card.Body>
              <Card.Title className="pb-3">🤝 Informations partenaire</Card.Title>
              <Row className="pb-3">
                <Col>
                  <p><strong>Partenaire : </strong>{orderData?.partner_token?.name || "—"}</p>
                </Col>
                <Col>
                  <p><strong>Email facturation : </strong>{orderData?.partner_token?.email || "—"}</p>
                </Col>
                <Col></Col>
              </Row>
              {(orderData?.merchant_name || orderData?.merchant_email || orderData?.merchant_phone) && (
                <Row className="pb-3">
                  <Col>
                    <p><strong>Fournisseur (point collecte) : </strong>{orderData?.merchant_name || "—"}</p>
                  </Col>
                  <Col>
                    <p><strong>Email fournisseur : </strong>{orderData?.merchant_email || "—"}</p>
                  </Col>
                  <Col>
                    <p><strong>Tél fournisseur : </strong>{orderData?.merchant_phone || "—"}</p>
                  </Col>
                </Row>
              )}
              {orderData?.purchase_order_file && (
                <Row className="pb-3">
                  <Col md={4}>
                    <p><strong>Bon de commande :</strong></p>
                    <a
                      href={`${urlPdf}/partner_orders/${orderData.purchase_order_file}`}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-outline-primary btn-sm"
                    >
                      📄 Télécharger le bon de commande
                    </a>
                  </Col>
                </Row>
              )}
            </Card.Body>
          </Card>
        )}

        {/* Bloc manutention — affiché uniquement si le client a pris des options de manutention */}
        {(orderData?.manutention_status || orderData?.manutention_total_ttc > 0) && (
          <Card className="mb-3 border-warning">
            <Card.Body>
              <Card.Title className="pb-3">🏗️ Options de manutention</Card.Title>
              <Row className="pb-3">
                <Col>
                  <p><strong>Étage : </strong>{orderData?.manutention_etage ?? "—"}</p>
                </Col>
                <Col>
                  <p><strong>Ascenseur : </strong>{libelleAscenseur(orderData?.manutention_ascenseur)}</p>
                </Col>
                <Col>
                  <p><strong>Marches estimées : </strong>{orderData?.manutention_marches_estimees ?? "—"}</p>
                </Col>
              </Row>
              <Row className="pb-3">
                <Col>
                  <p>
                    <strong>Distance de portage : </strong>{orderData?.manutention_distance ?? "—"}
                    {orderData?.manutention_distance_supplement > 0 && (
                      <> (supplément : {Number(orderData.manutention_distance_supplement).toFixed(2)} € HT)</>
                    )}
                  </p>
                </Col>
                <Col>
                  <p>
                    <strong>Obstacles : </strong>{orderData?.manutention_obstacles ?? "—"}
                    {orderData?.manutention_obstacles_supplement > 0 && (
                      <> (supplément : {Number(orderData.manutention_obstacles_supplement).toFixed(2)} € HT)</>
                    )}
                  </p>
                </Col>
                <Col>
                  <p>
                    <strong>Poids colis le plus lourd : </strong>{orderData?.manutention_poids_colis ?? "—"}
                    {orderData?.manutention_poids_colis_supplement > 0 && (
                      <> (supplément : {Number(orderData.manutention_poids_colis_supplement).toFixed(2)} € HT)</>
                    )}
                  </p>
                </Col>
              </Row>
              {orderData?.manutention_description_acces && (
                <Row className="pb-3">
                  <Col>
                    <p><strong>Description accès : </strong>{orderData.manutention_description_acces}</p>
                  </Col>
                </Row>
              )}
              <Row className="pb-3">
                <Col>
                  <p>
                    <strong>Supplément poids total : </strong>
                    {orderData?.manutention_poids_total_supplement > 0
                      ? `${Number(orderData.manutention_poids_total_supplement).toFixed(2)} € HT`
                      : "—"}
                  </p>
                </Col>
                <Col>
                  <p>
                    <strong>Total manutention : </strong>
                    {orderData?.manutention_status === "a_confirmer" ? (
                      <span className="text-warning fw-bold">À confirmer</span>
                    ) : (
                      <>
                        {Number(orderData?.manutention_total_ht ?? 0).toFixed(2)} € HT / {Number(orderData?.manutention_total_ttc ?? 0).toFixed(2)} € TTC
                      </>
                    )}
                  </p>
                </Col>
                <Col />
              </Row>
              {/* Qui a renseigné ces réponses. La saisie du livreur s'applique
                  directement au montant facturé : si le client conteste un
                  supplément des semaines plus tard, c'est ici qu'on lit
                  « constaté par le livreur le 14/09 ». */}
              {orderData?.manutention_saisie && (
                <Row className="pb-2">
                  <Col>
                    <p className="text-muted mb-0">
                      <small>
                        Renseigné par{" "}
                        <strong>
                          {LIBELLES_SAISIE_MANUTENTION[orderData.manutention_saisie.source] ??
                            orderData.manutention_saisie.libelle}
                        </strong>
                        {/* Le nom, quand on l'a : « le livreur » ne dit pas
                            lequel des trois, et c'est la question qu'on se pose
                            en relisant un montant. */}
                        {orderData.manutention_saisie.par && (
                          <> — {orderData.manutention_saisie.par}</>
                        )}
                        {orderData.manutention_saisie.le_texte && (
                          <>, le {orderData.manutention_saisie.le_texte}</>
                        )}
                      </small>
                      {/* Le visa de relecture. Il ne change pas le montant,
                          deja applique : il dit que quelqu'un l'a regarde. */}
                      {orderData?.manutention_visa ? (
                        <small className="d-block text-success">
                          Relu
                          {orderData.manutention_visa.par && <> par {orderData.manutention_visa.par}</>}
                          {" le "}{orderData.manutention_visa.le_texte}
                        </small>
                      ) : orderData.manutention_saisie.source === "livreur" && (
                        <small className="d-block text-warning">Pas encore relu par l'agence</small>
                      )}
                    </p>
                  </Col>
                </Row>
              )}
              {orderData?.manutention_status === "a_confirmer" && (
                <Row>
                  <Col>
                    <div className="alert alert-warning mb-0">
                      ⚠️ Manutention à confirmer manuellement avant livraison
                      {orderData?.manutention_saisie?.source === "livreur" && (
                        <>
                          {" — "}le livreur a décrit un accès que le barème ne tarife pas :
                          saisir un montant négocié.
                        </>
                      )}
                    </div>
                  </Col>
                </Row>
              )}
            </Card.Body>
          </Card>
        )}

        {/* Historique des changements de statut */}
        {orderData?.historique_statuts && orderData.historique_statuts.length > 0 && (
          <Card className="mb-3">
            <Card.Body>
              <Card.Title className="pb-3">Historique des changements de statut</Card.Title>
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Qui</th>
                    <th>Quand</th>
                    <th>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {orderData.historique_statuts.map((historique, index) => (
                    <tr key={index}>
                      <td>{historique.qui}</td>
                      <td>{historique.quand}</td>
                      <td>{historique.statut}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        )}

        {orderData?.Bordereau ? (
          <Card className="mb-3">
            <Card.Body>
              <Card.Title className="pb-3">Bordereau :</Card.Title>

              {/* L'aperçu n'avait ni largeur ni hauteur : l'iframe tombait sur sa
                  taille par défaut (300×150) dans une colonne au tiers, donc
                  illisible. Deux sorties explicites plutôt qu'un aperçu qu'on
                  plisse les yeux pour lire. */}
              <div className="d-flex gap-2 mb-3">
                <Button variant="outline-primary" size="sm" onClick={() => setBordereauModal(true)}>
                  Voir en grand
                </Button>
                {/* Nouvel onglet et non attribut `download` : le fichier est servi
                    par l'API, donc sur une autre origine que le back-office, et
                    `download` y est ignoré par le navigateur. */}
                <a
                  href={urlBordereau}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-secondary btn-sm"
                >
                  Ouvrir / télécharger
                </a>
              </div>

              <Row>
                <Col md={6}>
                  <Card>
                    <iframe
                      src={urlBordereau}
                      title="Bordereau de livraison"
                      style={{ width: "100%", height: "500px", border: "none" }}
                    />
                  </Card>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        ) : (
          <Card className="mb-3">
            <Card.Body>
              <Card.Title className="pb-3">Bordereau: -</Card.Title>
            </Card.Body>
          </Card>
        )}
        {/* Facture PDF */}
        {orderData?.NumeroFacture ? (
          <Card className="mb-3">
            <Card.Body>
              <Card.Title className="pb-3">Facture :</Card.Title>
              {orderData?.StatuPaiement === 3 && orderData?.StatuCommande !== 6 && (
                <Button
                  variant="danger"
                  size="sm"
                  className="mb-3"
                  onClick={() => { setAvoirError(""); setAvoirModal(true); }}
                >
                  Générer un avoir &amp; annuler
                </Button>
              )}
              <Row>
                <Col md={4}>
                  <Card>
                    <iframe
                      src={`${urlPdf}/factures/${orderData.NumeroFacture}`}
                      title="Facture"
                      style={{ width: "100%", height: "500px", border: "none" }}
                    />
                  </Card>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        ) : (
          <Card className="mb-3">
            <Card.Body>
              <Card.Title className="pb-3">Facture : -</Card.Title>
            </Card.Body>
          </Card>
        )}

        {/* Avoir PDF */}
        {orderData?.avoir_file && (
          <Card className="mb-3 border-danger">
            <Card.Body>
              <Card.Title className="pb-3 text-danger">
                Avoir — {orderData.avoir_number}
              </Card.Title>
              {orderData.cancellation_reason && (
                <p className="mb-3">
                  <strong>Motif d'annulation :</strong> {orderData.cancellation_reason}
                </p>
              )}
              <Row>
                <Col md={4}>
                  <Card>
                    <iframe
                      src={`${urlPdf}/factures/${orderData.avoir_file}`}
                      title="Avoir"
                      style={{ width: "100%", height: "500px", border: "none" }}
                    />
                  </Card>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        {/* Feedback ramassage / livraison */}
        <CommandeFeedback commandeId={idCommande} />

        {/* Section POD Images */}
        <PODImages commandeId={idCommande} readOnly={true} />
      </div>

      {/* Modale génération avoir */}
      <Modal show={avoirModal} onHide={() => setAvoirModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Générer un avoir</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Cette action <strong>rembourse intégralement le client via Stripe</strong> et annule la commande.
            L'avoir PDF sera envoyé par email au client.
          </p>
          <Form.Group className="mt-3">
            <Form.Label>
              Motif d'annulation <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={avoirMotif}
              onChange={(e) => setAvoirMotif(e.target.value)}
              placeholder="Ex : Article indisponible, erreur de commande, litige client..."
            />
          </Form.Group>
          {avoirError && <p className="text-danger mt-2">{avoirError}</p>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setAvoirModal(false)} disabled={avoirLoading}>
            Annuler
          </Button>
          <Button variant="danger" onClick={handleGenererAvoir} disabled={avoirLoading}>
            {avoirLoading ? <Spinner size="sm" animation="border" /> : "Confirmer l'avoir"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Avoir à montant libre. Distinct de la modale ci-dessus, qui rembourse
          la totalité via Stripe et annule la commande : ici on produit le seul
          document comptable, le remboursement éventuel restant une décision
          bancaire séparée. */}
      <Modal show={partielModal} onHide={() => setPartielModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Émettre un avoir</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted" style={{ fontSize: "0.9rem" }}>
            Produit un document d'avoir référençant la facture d'origine.{" "}
            <strong>Aucun remboursement n'est déclenché</strong> : si une somme doit être
            rendue, elle se fait séparément depuis la banque ou Stripe.
          </p>

          <Form.Group className="mt-3">
            <Form.Label>
              Montant HT à régulariser <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              inputMode="decimal"
              value={partielMontant}
              onChange={(e) => setPartielMontant(e.target.value)}
              placeholder="Ex : 70,00"
            />
            <Form.Text className="text-muted">
              Montant hors taxes. La TVA ({orderData?.TVA ?? 20} %) est ajoutée sur le document.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mt-3">
            <Form.Label>
              Motif <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={partielMotif}
              onChange={(e) => setPartielMotif(e.target.value)}
              placeholder="Ex : remise commerciale non appliquée sur la facture d'origine"
            />
          </Form.Group>

          {partielError && <p className="text-danger mt-2">{partielError}</p>}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setPartielModal(false)}
            disabled={partielLoading}
          >
            Annuler
          </Button>
          <Button variant="warning" onClick={handleAvoirPartiel} disabled={partielLoading}>
            {partielLoading ? <Spinner size="sm" animation="border" /> : "Émettre l'avoir"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Bordereau en grand. `fullscreen` plutot qu'une taille fixe : un bon de
          livraison est souvent une photo prise de travers, et c'est la place a
          l'ecran qui le rend lisible, pas un cadre soigne. */}
      <Modal show={bordereauModal} onHide={() => setBordereauModal(false)} fullscreen>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1.1rem" }}>
            Bordereau — livraison n° {orderData?.id}
            {urlBordereau && (
              <a
                href={urlBordereau}
                target="_blank"
                rel="noopener noreferrer"
                className="ms-3 fs-6"
              >
                Ouvrir dans un onglet
              </a>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0 bg-dark d-flex align-items-center justify-content-center">
          {urlBordereau && (bordereauEstImage ? (
            <img
              src={urlBordereau}
              alt={`Bordereau de la livraison ${orderData?.id}`}
              style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
            />
          ) : (
            <iframe
              src={urlBordereau}
              title="Bordereau de livraison en grand"
              style={{ width: "100%", height: "100%", border: "none" }}
            />
          ))}
        </Modal.Body>
      </Modal>
    </Container>
  );
}

export default ShowCommandeFournisseur;
